/**
 * Order Service - Frontend service for order operations
 */

import * as orderRepository from "../repositories/orderRepository";
import { logError } from "../../../shared/utils/logger.js";

export const getOrders = async (filters = {}) => {
  const response = await orderRepository.fetchOrders(filters);
  if (!response.success)
    throw new Error(response.error || "Failed to fetch orders");
  return response;
};

/**
 * Get orders by trip ID
 * @param {string} tripId - Trip identifier
 * @returns {Promise<Array>} Array of orders
 */
export const getOrdersByTrip = async (tripId) => {
  try {
    const response = await getOrders({ tripId });
    if (!response.success) {
      throw new Error(response.error || "Failed to fetch orders by trip");
    }
    return response.data || [];
  } catch (error) {
    logError("Failed to fetch orders by trip", {
      tripId,
      error: error.message,
    });
    throw error;
  }
};

export const getOrder = async (orderId) => {
  const response = await orderRepository.fetchOrderById(orderId);
  if (!response.success)
    throw new Error(response.error || "Failed to fetch order");
  return response.data;
};

export const getOrderCompletionEligibility = async (orderId) => {
  const response = await orderRepository.fetchCompletionEligibility(orderId);
  if (!response.success)
    throw new Error(response.error || "Failed to check completion eligibility");
  return response.data;
};

export const createOrder = async (orderData) => {
  const response = await orderRepository.createOrder(orderData);
  if (!response.success)
    throw new Error(response.error || "Failed to create order");
  return response;
};

export const createOrderFromQuote = async (quoteData) => {
  const response = await orderRepository.createOrderFromQuote(quoteData);
  if (!response.success)
    throw new Error(response.error || "Failed to create order from quote");
  return response.data;
};

export const updateOrder = async (orderId, updateData) => {
  const response = await orderRepository.updateOrder(orderId, updateData);
  if (!response.success)
    throw new Error(response.error || "Failed to update order");
  return response;
};

export const deleteOrder = async (orderId) => {
  const response = await orderRepository.deleteOrder(orderId);
  if (!response.success)
    throw new Error(response.error || "Failed to delete order");
  return response;
};

export const updateOrderStatus = async (orderId, status, context = {}) => {
  const response = await orderRepository.updateOrderStatus(orderId, status, context);
  if (!response.success)
    throw new Error(response.error || "Failed to update order status");
  return response.data;
};

export const assignOrder = async (orderId, vehicleId, driverId) => {
  const response = await orderRepository.assignOrder(
    orderId,
    vehicleId,
    driverId
  );
  if (!response.success)
    throw new Error(response.error || "Failed to assign order");
  return response.data;
};

/**
 * Get order statistics
 * @returns {Promise<Object>} Order statistics
 */
export const getOrderStats = async () => {
  try {
    const orders = await getOrders();
    if (!orders.success) {
      throw new Error(orders.error);
    }

    const allOrders = orders.data || [];

    const stats = {
      total: allOrders.length,
      pending: allOrders.filter((order) => order.status === "pending").length,
      inProgress: allOrders.filter((order) => order.status === "in_progress")
        .length,
      completed: allOrders.filter((order) => order.status === "completed")
        .length,
      cancelled: allOrders.filter((order) => order.status === "cancelled")
        .length,
    };

    return {
      success: true,
      data: stats,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Active Deliveries operational summary (Phase 2 server health)
 * @returns {Promise<Object>}
 */
export const getOperationalSummary = async () => {
  try {
    const response = await orderRepository.fetchOperationalSummary();
    if (!response.success) {
      throw new Error(response.error || "Failed to fetch operational summary");
    }
    return response;
  } catch (error) {
    logError("Failed to fetch operational summary", { error: error.message });
    return { success: false, error: error.message };
  }
};

/**
 * Get status templates (WhatsApp and Email) for an order status
 * @param {string} orderId - Order identifier
 * @param {string} status - Order status
 * @param {Object} [context] - Additional context data
 * @returns {Promise<Object>} Templates object
 */
export const getStatusTemplates = async (orderId, status, context = {}) => {
  try {
    const response = await orderRepository.fetchStatusTemplates(
      orderId,
      status,
      context
    );
    if (!response.success) {
      return {
        success: false,
        error: response.error || "Failed to fetch status templates",
      };
    }
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Error fetching status templates", {
      status,
      error: error.message,
      statusCode: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch status templates",
    };
  }
};

/**
 * Get status communications tracking for an order
 * @param {string} orderId - Order identifier
 * @returns {Promise<Object>} Status communications object
 */
export const getStatusCommunications = async (orderId) => {
  try {
    const response = await orderRepository.fetchStatusCommunications(orderId);
    if (!response.success) {
      return {
        success: false,
        error: response.error || "Failed to fetch status communications",
      };
    }
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Error fetching status communications", {
      orderId,
      error: error.message,
      statusCode: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch status communications",
    };
  }
};

/**
 * Update status communication tracking
 * @param {string} orderId - Order identifier
 * @param {string} status - Order status
 * @param {Object} communicationData - Communication data
 * @param {boolean} [communicationData.whatsappSent] - WhatsApp sent
 * @param {boolean} [communicationData.emailSent] - Email sent
 * @returns {Promise<Object>} Updated order
 */
export const updateStatusCommunication = async (
  orderId,
  status,
  communicationData
) => {
  try {
    const response = await orderRepository.updateStatusCommunication(
      orderId,
      status,
      communicationData
    );
    if (!response.success) {
      return {
        success: false,
        error: response.error || "Failed to update status communication",
      };
    }
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Error updating status communication", {
      orderId,
      status,
      communicationData,
      error: error.message,
      statusCode: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to update status communication",
    };
  }
};

/**
 * Send partner job pack (PDF with selected orders)
 * @param {Object} data - Job pack data
 * @param {string} data.partnerId - Partner ID
 * @param {Array<string>} data.orderIds - Array of order IDs to include
 * @returns {Promise<Object>} Send result
 */
export const sendPartnerJobPack = async (data) => {
  try {
    const response = await orderRepository.sendPartnerJobPack(data);
    if (!response.success) {
      return {
        success: false,
        error: response.error || "Failed to send partner job pack",
      };
    }
    return {
      success: true,
      jobId: response.jobId,
      message: response.message || "Job pack sent successfully",
    };
  } catch (error) {
    logError("Error sending partner job pack", {
      partnerId: data.partnerId,
      orderIds: data.orderIds,
      error: error.message,
      statusCode: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to send partner job pack",
    };
  }
};

/**
 * Preview partner job pack PDF
 * @param {Object} data - Preview data
 * @param {Array<string>} data.orderIds - Array of order IDs to include
 * @returns {Promise<Object>} Preview result with PDF URL
 */
export const previewPartnerJobPack = async (data) => {
  try {
    const response = await orderRepository.previewPartnerJobPack(data);
    if (!response.success) {
      return {
        success: false,
        error: response.error || "Failed to generate preview",
      };
    }
    return {
      success: true,
      pdfUrl: response.pdfUrl,
    };
  } catch (error) {
    logError("Error previewing partner job pack", {
      orderIds: data.orderIds,
      error: error.message,
      statusCode: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to generate preview",
    };
  }
};

/**
 * Get order participants (drivers, partners, suppliers, internal staff, contacts)
 * @param {string} orderId - Order identifier
 * @returns {Promise<Object>} Participants data
 */
export const getOrderParticipants = async (orderId) => {
  try {
    const response = await orderRepository.fetchOrderParticipants(orderId);
    if (!response.success) {
      return {
        success: false,
        error: response.error || "Failed to fetch participants",
      };
    }
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Error fetching order participants", {
      orderId,
      error: error.message,
      statusCode: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch participants",
    };
  }
};

/**
 * Get or issue a shareable customer tracking link (capability token)
 * @param {string} orderId
 * @returns {Promise<{ trackingUrl: string, trackingTokenId: string, expiresAt?: string, reused?: boolean }>}
 */
export const getOrderTrackingLink = async (orderId) => {
  const response = await orderRepository.fetchOrderTrackingLink(orderId);
  if (!response.success) {
    throw new Error(response.error || "Failed to fetch tracking link");
  }
  return response.data;
};

/**
 * @param {string} orderId
 * @param {{ q?: string, limit?: number, skip?: number }} params
 */
export const getInvoiceLinkCandidates = async (orderId, params = {}) => {
  try {
    const response = await orderRepository.fetchInvoiceLinkCandidates(orderId, params);
    if (!response?.success) {
      return {
        success: false,
        error: response?.error || "Failed to load invoice candidates",
      };
    }
    return { success: true, data: response.data };
  } catch (error) {
    logError("Error fetching invoice link candidates", {
      orderId,
      error: error.message,
    });
    return {
      success: false,
      error: error.message || "Failed to load invoice candidates",
    };
  }
};

/**
 * @param {string} orderId
 * @param {{ invoiceId: string, allowCustomerMismatch?: boolean, forceHardLink?: boolean }} body
 */
export const linkInvoiceToOrder = async (orderId, body) => {
  try {
    const response = await orderRepository.postLinkInvoiceToOrder(orderId, body);
    if (!response?.success) {
      return {
        success: false,
        error: response?.error || "Failed to link invoice",
        code: response?.code,
        existingJobId: response?.existingJobId,
      };
    }
    return { success: true, data: response.data };
  } catch (error) {
    const data = error.response?.data || {};
    logError("Error linking invoice to order", {
      orderId,
      error: error.message,
      code: data.code,
    });
    return {
      success: false,
      error: data.error || error.message || "Failed to link invoice",
      code: data.code,
      existingJobId: data.existingJobId,
    };
  }
};
